import React, { useEffect, useRef } from 'react';
import { Avatar } from '../ui.jsx';

const TEACHER_NAV = [
  { sec: 'Main', items: [{ id: 'tdash', icon: 'dashboard', label: 'Dashboard' }] },
  {
    sec: 'Academics', items: [{ id: 'tatt', icon: 'fact_check', label: 'Attendance' },
    { id: 'texam', icon: 'quiz', label: 'Exams & Grades' },
    { id: 'ttt', icon: 'schedule', label: 'Timetable' },
    { id: 'tcal', icon: 'calendar_month', label: 'Calendar' }]
  },
  { sec: 'People', items: [{ id: 'tstu', icon: 'group', label: 'My Students' }] },
  {
    sec: 'More', items: [{ id: 'tmyatt', icon: 'co_present', label: 'My Attendance' },
    { id: 'tmsg', icon: 'mail', label: 'Messages' },
    { id: 'tprofile', icon: 'person', label: 'My Profile' }]
  },
];

// Teacher-specific Sidebar (same visual as admin Sidebar but uses TEACHER_NAV)
const TeacherSidebar = React.memo(function TeacherSidebar({ page, setPage, user, db, onLogout, isMobile = false, mobileOpen = false, onMobileClose = () => { } }) {
  const ref = React.useRef(null);
  const open = React.useRef(false);

  const expand = () => {
    if (isMobile) return;
    if (open.current) return;
    open.current = true;
    const el = ref.current; if (!el) return;
    el.style.width = '230px';
    el.querySelectorAll('.sb-label').forEach(n => { n.style.opacity = '1'; n.style.maxWidth = '180px'; });
    el.querySelectorAll('.sb-sec').forEach(n => { n.style.opacity = '1'; n.style.maxHeight = '24px'; });
    el.querySelectorAll('.sb-btn').forEach(n => { n.style.justifyContent = 'flex-start'; n.style.padding = '10px 12px'; });
    el.querySelectorAll('.sb-user').forEach(n => { n.style.opacity = '1'; n.style.maxWidth = '160px'; });
    el.querySelectorAll('.sb-active-pill').forEach(n => { n.style.opacity = '1'; });
  };

  const collapse = () => {
    if (isMobile) return;
    if (!open.current) return;
    open.current = false;
    const el = ref.current; if (!el) return;
    el.style.width = '60px';
    el.querySelectorAll('.sb-label').forEach(n => { n.style.opacity = '0'; n.style.maxWidth = '0'; });
    el.querySelectorAll('.sb-sec').forEach(n => { n.style.opacity = '0'; n.style.maxHeight = '0'; });
    el.querySelectorAll('.sb-btn').forEach(n => { n.style.justifyContent = 'center'; n.style.padding = '10px 0'; });
    el.querySelectorAll('.sb-user').forEach(n => { n.style.opacity = '0'; n.style.maxWidth = '0'; });
    el.querySelectorAll('.sb-active-pill').forEach(n => { n.style.opacity = '0'; });
  };

  const secColor = { Main: '#63b3ff', People: '#86efac', Academics: '#fbbf24', Finance: '#f9a8d4', More: '#c4b5fd' };
  const name = (user.fn || '') + ' ' + (user.ln || '');
  const initials = name.trim().split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || 'T';
  const sidebarPhoto = (db.tphotos || {})[user.tid] || (db.tphotos || {})[user.id] ||
    (() => { const t = (db.teachers || []).find(t => (t.puser || t.username) === (user.puser || user.username)); return t ? (db.tphotos || {})[t.tid || t.id] : null; })() || null;

  React.useEffect(() => {
    const el = ref.current; if (!el) return;
    if (isMobile) {
      el.querySelectorAll('.sb-label').forEach(n => { n.style.opacity = '1'; n.style.maxWidth = '180px'; });
      el.querySelectorAll('.sb-sec').forEach(n => { n.style.opacity = '1'; n.style.maxHeight = '24px'; });
      el.querySelectorAll('.sb-btn').forEach(n => { n.style.justifyContent = 'flex-start'; n.style.padding = '10px 12px'; });
      el.querySelectorAll('.sb-user').forEach(n => { n.style.opacity = '1'; n.style.maxWidth = '160px'; });
      el.querySelectorAll('.sb-active-pill').forEach(n => { n.style.opacity = '1'; });
      open.current = true;
      return;
    }
    collapse();
  }, [isMobile]);

  const doLogout = () => { onLogout(); if (isMobile) onMobileClose(); };

  return (
    <aside ref={ref} onMouseEnter={isMobile ? undefined : expand} onMouseLeave={isMobile ? undefined : collapse}
      style={{
        width: isMobile ? 230 : 60, flexShrink: 0, zIndex: isMobile ? 1200 : 50, overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        position: isMobile ? 'fixed' : 'relative',
        left: 0, top: isMobile ? 0 : undefined, bottom: isMobile ? 0 : undefined,
        transform: isMobile ? (mobileOpen ? 'translateX(0)' : 'translateX(-110%)') : 'none',
        transition: isMobile ? 'transform 220ms ease' : 'width 260ms cubic-bezier(0.4,0,0.2,1)',
        backgroundColor: '#1a3a5c',
        backgroundImage: `
          radial-gradient(ellipse at 0% 0%, rgba(99,179,255,0.12) 0%, transparent 60%),
          radial-gradient(ellipse at 100% 100%, rgba(99,102,241,0.1) 0%, transparent 60%),
          url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='1' cy='1' r='0.8' fill='%23ffffff' fill-opacity='0.04'/%3E%3C/svg%3E")
        `,
        backgroundSize: 'auto,auto,24px 24px',
        boxShadow: '4px 0 20px rgba(0,20,60,0.18)',
      }}>

      {/* Logo row */}
      {/* Teacher profile row — replaces logo, clickable → profile page */}
      <div onClick={() => { setPage('tprofile'); if (isMobile) onMobileClose(); }}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '12px 0', flexShrink: 0,
          borderBottom: '1px solid rgba(255,255,255,0.07)', cursor: 'pointer', transition: 'background 150ms'
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(96,165,250,0.08)'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
        {/* Avatar */}
        <div style={{
          width: 34, height: 34, minWidth: 34, borderRadius: '50%',
          background: 'linear-gradient(135deg,#059669,#10b981)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, fontWeight: 800, color: '#fff', flexShrink: 0, overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(5,150,105,0.35)'
        }}>
          {sidebarPhoto
            ? <img src={sidebarPhoto} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : initials}
        </div>
        {/* Name + role — hidden when collapsed */}
        <div className="sb-label" style={{ opacity: 0, maxWidth: 0, overflow: 'hidden', transition: 'opacity 220ms,max-width 260ms', whiteSpace: 'nowrap', flexShrink: 0, minWidth: 0 }}>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis' }}>{name.trim() || 'Teacher'}</div>
          <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 1 }}>{user.desig || user.su || 'Faculty'}</div>
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '8px 6px 0', scrollbarWidth: 'none' }}>
        <style>{`aside::-webkit-scrollbar{display:none}`}</style>
        {TEACHER_NAV.map((g, gi) => (
          <div key={g.sec} style={{ marginBottom: 4 }}>
            <div className="sb-sec" style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 10px 4px',
              opacity: 0, maxHeight: 0, overflow: 'hidden',
              transition: 'opacity 200ms,max-height 240ms',
            }}>
              <div style={{ height: 1, width: 10, background: secColor[g.sec] || 'rgba(255,255,255,0.2)', borderRadius: 1, flexShrink: 0 }} />
              <span style={{ color: secColor[g.sec] || 'rgba(255,255,255,0.3)', fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', whiteSpace: 'nowrap' }}>{g.sec}</span>
            </div>
            {g.items.map(it => {
              const active = page === it.id;
              return (
                <button key={it.id} onClick={() => { setPage(it.id); if (isMobile) onMobileClose(); }}
                  title={it.label}
                  className="sb-btn"
                  style={{
                    position: 'relative', display: 'flex', alignItems: 'center', width: '100%',
                    border: 'none', borderRadius: 10, cursor: 'pointer', marginBottom: 2,
                    padding: '10px 0', justifyContent: 'center', gap: 0,
                    background: active ? 'rgba(255,255,255,0.12)' : 'transparent',
                    color: active ? '#fff' : 'rgba(255,255,255,0.5)',
                    fontSize: 13, fontWeight: 600, fontFamily: 'Manrope,sans-serif',
                    transition: 'background 180ms,color 180ms,transform 120ms',
                    boxShadow: active ? 'inset 0 0 0 1px rgba(255,255,255,0.1)' : 'none',
                    minHeight: 40,
                  }}
                  onMouseEnter={e => { if (!active) { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; e.currentTarget.style.transform = 'translateX(2px)'; } }}
                  onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; e.currentTarget.style.transform = 'translateX(0)'; } }}
                >
                  {active && <div style={{ position: 'absolute', left: 0, top: '20%', bottom: '20%', width: 3, borderRadius: '0 3px 3px 0', background: secColor[g.sec] || '#63b3ff', boxShadow: `0 0 8px ${secColor[g.sec] || '#63b3ff'}88`, pointerEvents: 'none' }} />}
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 44, minWidth: 44, flexShrink: 0, flexGrow: 0 }}>
                    <span className="material-symbols-outlined" style={{
                      fontSize: 20, lineHeight: 1,
                      color: active ? (secColor[g.sec] || '#63b3ff') : 'inherit',
                      filter: active ? `drop-shadow(0 0 4px ${secColor[g.sec] || '#63b3ff'}88)` : 'none',
                      transition: 'color 180ms,filter 180ms',
                    }}>{it.icon}</span>
                  </span>
                  <span className="sb-label" style={{ whiteSpace: 'nowrap', opacity: 0, maxWidth: 0, overflow: 'hidden', transition: 'opacity 200ms,max-width 240ms', textAlign: 'left', flex: 1 }}>{it.label}</span>
                  <span className="sb-active-pill" style={{
                    display: active ? 'block' : 'none',
                    marginRight: 10,
                    width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                    background: secColor[g.sec] || '#63b3ff',
                    boxShadow: `0 0 6px ${secColor[g.sec] || '#63b3ff'}`,
                    opacity: 0, transition: 'opacity 200ms',
                  }} />
                </button>
              );
            })}
            {gi < TEACHER_NAV.length - 1 && (
              <div style={{ height: 1, background: 'rgba(255,255,255,0.05)', margin: '6px 8px' }} />
            )}
          </div>
        ))}
      </nav>

      {/* Footer — logout only */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', padding: '8px 8px', flexShrink: 0 }}>
        <button className="sb-btn" onClick={doLogout} title="Sign Out"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', border: 'none',
            borderRadius: 10, cursor: 'pointer', padding: '10px 0', gap: 0,
            background: 'transparent', color: 'rgba(255,255,255,0.45)',
            transition: 'all 150ms', minHeight: 40, fontSize: 13, fontWeight: 600, fontFamily: 'Manrope,sans-serif'
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.15)'; e.currentTarget.style.color = '#fca5a5'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.45)'; }}>
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 44, minWidth: 44, flexShrink: 0 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 20, lineHeight: 1 }}>logout</span>
          </span>
          <span className="sb-label" style={{ whiteSpace: 'nowrap', opacity: 0, maxWidth: 0, overflow: 'hidden', transition: 'opacity 200ms,max-width 240ms', textAlign: 'left', flex: 1 }}>Sign Out</span>
        </button>
      </div>
    </aside>
  );
});


export default TeacherSidebar;
